/**
 * @file ProgressPresenter.ts
 * @role Display-only smoothing for the splash progress bar: eases toward the real loading
 *       fraction, creeps slightly ahead during network stalls so the bar never looks frozen,
 *       and never moves backward. Purely presentational — the truthful per-bundle progress
 *       keeps flowing on the EventBus untouched, and scene transitions stay driven by the
 *       real load promises, never by this value.
 * @owns The displayed progress value + its easing/creep/complete behavior.
 * @reads setTarget(real01) from the scene; clamped GameTicker dt via update().
 * @neverDoes Gate loading. Emit events. Report fake completion (1 requires a real target of 1).
 * @invariants 22 (advanced only by the scene's clamped update dt).
 * @see src/scenes/SplashScene/SplashScene.ts
 */

export interface ProgressPresenterOptions {
  /** Exponential catch-up rate toward the real target (per second). */
  catchUpPerSecond: number;
  /** Linear creep speed during a stall (fraction per second). */
  creepPerSecond: number;
  /** How far past the real target the stall creep may drift. */
  creepHeadroom: number;
  /** Exponential rate toward 1 once the real target reports complete (per second). */
  completePerSecond: number;
}

const DEFAULTS: ProgressPresenterOptions = {
  catchUpPerSecond: 4,
  creepPerSecond: 0.01,
  creepHeadroom: 0.08,
  completePerSecond: 6,
};

export class ProgressPresenter {
  private display = 0;
  private target = 0;

  constructor(private readonly options: ProgressPresenterOptions = DEFAULTS) {}

  get displayed(): number {
    return this.display;
  }

  /** Report the real loading fraction. Backward reports are ignored (display never regresses). */
  setTarget(target01: number): void {
    this.target = Math.max(this.target, Math.min(1, Math.max(0, target01)));
  }

  /** Advance the displayed value by clamped dt; returns the new display fraction. */
  update(deltaMs: number): number {
    const dtSeconds = Math.max(0, deltaMs) / 1000;
    if (this.target >= 1) {
      // Real completion: ease to the end and snap when visually indistinguishable.
      const next = this.approach(this.display, 1, this.options.completePerSecond, dtSeconds);
      this.display = next > 0.995 ? 1 : next;
    } else if (this.display < this.target) {
      this.display = this.approach(
        this.display,
        this.target,
        this.options.catchUpPerSecond,
        dtSeconds,
      );
    } else {
      // Stall: keep perceptible motion, but stay honest — never drift past the headroom.
      const ceiling = Math.min(this.target + this.options.creepHeadroom, 0.95);
      this.display = Math.min(ceiling, this.display + this.options.creepPerSecond * dtSeconds);
    }
    return this.display;
  }

  private approach(current: number, goal: number, ratePerSecond: number, dtSeconds: number): number {
    return current + (goal - current) * (1 - Math.exp(-ratePerSecond * dtSeconds));
  }
}
